import {Fragment} from 'react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {formatUsd, getPlan} from '@client/pages/public/enroll/enrollModel';
import {BENEFIT_CATEGORY_COLORS, BENEFIT_CATEGORY_LABELS} from '@client/pages/public/enroll/types';
import type {BenefitRow, GroupConfig} from '@client/pages/public/enroll/types';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';

const FALLBACK_CATEGORY_COLOR = '#94a3b8';

interface BenefitsTableProps {
  group: GroupConfig;
  planIds: string[];
  rows: BenefitRow[];
  selectedPlanId?: string;
}

interface CategoryGroup {
  category: string;
  rows: BenefitRow[];
}

function groupByCategory(rows: BenefitRow[]): CategoryGroup[] {
  const groups: CategoryGroup[] = [];
  for (const row of rows) {
    const existing = groups.find((g) => g.category === row.category);
    if (existing) existing.rows.push(row);
    else groups.push({category: row.category, rows: [row]});
  }
  return groups;
}

export function BenefitsTable({group, planIds, rows, selectedPlanId}: BenefitsTableProps) {
  const {getTier} = useEnrollSteps();
  const payPeriod = sitePayPeriod(useEnrollGroup());
  const plans = planIds
    .map((id) => getPlan(group, id))
    .filter((plan): plan is NonNullable<typeof plan> => Boolean(plan));

  if (plans.length === 0 || rows.length === 0) return null;

  // All plans in a table share one product category, so they price at the same tier.
  const tier = getTier(plans[0].category);

  const categoryGroups = groupByCategory(rows);

  const planCellStyle = (accent: string, planId: string): React.CSSProperties => ({
    backgroundColor: `color-mix(in srgb, ${accent} ${planId === selectedPlanId ? 18 : 8}%, white)`,
  });

  const planColWidth = plans.length === 1 ? 'w-2/5' : 'w-48';

  return (
    <div className={cn('overflow-x-auto rounded-xl border border-neutral-300', POSTHOG_UNMASK_CLASS)}>
      <table className='w-full min-w-[640px] border-separate border-spacing-0 text-left text-sm'>
        <thead>
          <tr>
            <th className='border-t-4 border-transparent px-4 py-3 align-top text-xs font-bold uppercase tracking-wide text-neutral-500'>
              Benefit
            </th>
            {plans.map((plan) => {
              const accent = plan.accentColor;
              return (
                <th
                  key={plan.id}
                  className={cn(
                    'border-l border-neutral-300 border-t-4 px-4 py-3 text-right align-top',
                    planColWidth,
                  )}
                  style={{borderTopColor: accent}}
                >
                  <span className='flex items-center justify-end gap-2'>
                    <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{backgroundColor: accent}} />
                    <span className='font-semibold text-neutral-800'>{plan.name}</span>
                  </span>
                  <span className='mt-0.5 block text-xs font-normal text-neutral-400'>
                    {formatUsd(plan.rates[tier])}
                    {payPeriod.priceSuffix}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {categoryGroups.map(({category, rows: categoryRows}) => (
            <Fragment key={category}>
              <tr>
                <td className='border-t border-neutral-300 px-4 py-2'>
                  <span
                    className='flex items-center gap-2 text-xs font-bold uppercase tracking-wide'
                    style={{color: BENEFIT_CATEGORY_COLORS[category] ?? FALLBACK_CATEGORY_COLOR}}
                  >
                    <span
                      className='h-2 w-2 shrink-0 rounded-full'
                      style={{backgroundColor: BENEFIT_CATEGORY_COLORS[category] ?? FALLBACK_CATEGORY_COLOR}}
                    />
                    {BENEFIT_CATEGORY_LABELS[category] ?? category}
                  </span>
                </td>
                {plans.map((plan) => (
                  <td
                    key={plan.id}
                    className={cn('border-l border-t border-neutral-300', planColWidth)}
                    style={planCellStyle(plan.accentColor, plan.id)}
                  />
                ))}
              </tr>
              {categoryRows.map((row) => (
                <tr key={row.label}>
                  <th
                    scope='row'
                    className='border-t border-neutral-300 px-4 py-3 font-medium text-neutral-700'
                  >
                    {row.label}
                  </th>
                  {plans.map((plan) => (
                    <td
                      key={plan.id}
                      className={cn(
                        'border-l border-t border-neutral-300 px-4 py-3 text-right text-neutral-600',
                        planColWidth,
                      )}
                      style={planCellStyle(plan.accentColor, plan.id)}
                    >
                      {row.values[plan.id] ?? <span className='text-neutral-300'>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
